import {
  ComputedFieldResourceType,
  type ComputedFieldComputation,
  type ComputedFieldComputationType,
} from "@repo/app-types/entities/computedField";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { useState, type FC } from "react";
import { Dialog } from "react-aria-components";
import { useCreateComputedFieldDefinitionMutation } from "../../../api/entities/computedField.js";
import {
  ComputedFieldDefinitionForm,
  parseCommaSeparatedValues,
} from "./computedFieldDefinitionForm.js";

export interface ComputedFieldDefinitionCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Dialog shell — opens iff `isOpen` is true, and unmounts the form when
 * it closes so form state is naturally reset on each open (no explicit
 * `resetForm()` plumbing).
 */
export const ComputedFieldDefinitionCreateDialog: FC<
  ComputedFieldDefinitionCreateDialogProps
> = ({ isOpen, onClose }) => {
  return (
    <CenteredModal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
      size={Size.LARGE}
    >
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
        {() => (
          <>
            <ModalHeader isCloseVisible>Create computed field</ModalHeader>
            <ComputedFieldDefinitionCreateForm onClose={onClose} />
          </>
        )}
      </Dialog>
    </CenteredModal>
  );
};

interface ComputedFieldDefinitionCreateFormProps {
  onClose: () => void;
}

const ComputedFieldDefinitionCreateForm: FC<
  ComputedFieldDefinitionCreateFormProps
> = ({ onClose }) => {
  const [createComputedFieldDefinition, { isLoading }] =
    useCreateComputedFieldDefinitionMutation();

  const [resourceType, setResourceType] = useState<ComputedFieldResourceType>(
    ComputedFieldResourceType.CONTACT,
  );
  const [displayName, setDisplayName] = useState("");
  const [computationType, setComputationType] =
    useState<ComputedFieldComputationType>("count_in");
  const [sourceCustomFieldId, setSourceCustomFieldId] = useState<string | null>(
    null,
  );
  const [values, setValues] = useState("");
  const [numeratorValues, setNumeratorValues] = useState("");
  const [denominatorValues, setDenominatorValues] = useState("");
  const [error, setError] = useState<string | null>(null);

  const computation: ComputedFieldComputation | null = (() => {
    if (!sourceCustomFieldId) return null;
    if (computationType === "count_in") {
      const parsed = parseCommaSeparatedValues(values);
      if (parsed.length === 0) return null;
      return {
        type: "count_in",
        source_custom_field_definition_id: sourceCustomFieldId,
        values: parsed,
      };
    }

    const numerator = parseCommaSeparatedValues(numeratorValues);
    const denominator = parseCommaSeparatedValues(denominatorValues);
    if (denominator.length === 0) return null;
    return {
      type: "rate_in",
      source_custom_field_definition_id: sourceCustomFieldId,
      numerator_values: numerator,
      denominator_values: denominator,
    };
  })();

  const isFormValid = displayName.trim() !== "" && computation !== null;

  const handleResourceTypeChange = (value: ComputedFieldResourceType) => {
    if (value !== resourceType) {
      // The eligible source fields depend on the resource type.
      setSourceCustomFieldId(null);
    }
    setResourceType(value);
  };

  const handleSubmit = async () => {
    if (!isFormValid || !computation) return;
    try {
      await createComputedFieldDefinition({
        display_name: displayName.trim(),
        resource_type: resourceType,
        computation,
      }).unwrap();
      onClose();
    } catch (err: any) {
      console.error("Failed to create computed field definition:", err);
      if (err.status === 409) {
        setError(
          err.data?.error || "A computed field with this name already exists.",
        );
      } else if (err.status === 400) {
        setError(err.data?.error || "Invalid computed field configuration.");
      } else {
        setError("Failed to create computed field. Please try again.");
      }
    }
  };

  return (
    <>
      <div className="my-4 space-y-4">
        <ComputedFieldDefinitionForm
          resourceType={resourceType}
          displayName={displayName}
          computationType={computationType}
          sourceCustomFieldDefinitionId={sourceCustomFieldId}
          values={values}
          numeratorValues={numeratorValues}
          denominatorValues={denominatorValues}
          onResourceTypeChange={handleResourceTypeChange}
          onDisplayNameChange={setDisplayName}
          onComputationTypeChange={setComputationType}
          onSourceCustomFieldDefinitionIdChange={setSourceCustomFieldId}
          onValuesChange={setValues}
          onNumeratorValuesChange={setNumeratorValues}
          onDenominatorValuesChange={setDenominatorValues}
        />

        {error && <div className="text-sm text-destructive">{error}</div>}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={!isFormValid}
        >
          Create computed field
        </Button>
      </div>
    </>
  );
};
